import React, { useMemo, useState } from 'react';

import { Icon } from 'components';
import Avatar from 'components/Avatar';
import { ButtonV3 } from 'components/ComponentV2';
import { reviewOfferModalInstance } from 'components/ReviewOfferModal';
import { useSponsorCompanies } from 'lib/providers/SocietySponsorshipProvider';
import { SponsorCompanyAttributes } from 'lib/models/sponsor-companies';
import { parseResponse } from 'lib/utils/parser';

import styles from './NotifySponsorBanner.module.scss';

interface NotifySponsorBannerProps {
  sponsorData: SponsorCompanyAttributes;
  type: 'invitor_onboarded' | 'request_company' | 'request_declined';
  onSelectPlan: () => void;
}

export const NotifySponsorBanner: React.FC<NotifySponsorBannerProps> = (
  props
) => {
  const { sponsorData, type, onSelectPlan } = props;
  const { id, company: CompanyData } = sponsorData;
  const { icon_url, name: companyName } = parseResponse(CompanyData);
  const { onCloseNotification } = useSponsorCompanies();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const bannerData = useMemo(() => {
    switch (type) {
      case 'invitor_onboarded':
        return {
          title: 'Invitation accepted',
          message: `has accepted your invitation for sponsorship.`,
          actionText: 'Select plan',
          action: onSelectPlan,
        };
      case 'request_company':
        return {
          title: 'You have new offer',
          message: `wants to be your sponsor`,
          actionText: 'Review',
          action: () =>
            reviewOfferModalInstance({ invite: sponsorData, isSociety: true }),
        };
      case 'request_declined':
        return {
          title: 'Request declined',
          message: `has declined your request for sponsorship.`,
          actionText: 'Close',
          action: () => markNotificationSeen(),
        };
      default:
        return {
          title: '',
          message: '',
          actionText: '',
          action: () => {},
        };
    }
  }, [type]);

  const markNotificationSeen = async () => {
    setIsLoading(true);
    await onCloseNotification(id);
    setIsLoading(false);
  };

  return (
    <>
      <div className={styles.NotifySponsorBanner}>
        <div className={styles.NotifySponsorBannerInfo}>
          <Icon iconName="icon_info" size="large" />
          <div className={styles.details}>
            <p className={styles.notifyTitle}>{bannerData.title}</p>
            <div className={styles.companyDetails}>
              <Avatar
                icon={icon_url}
                name={companyName}
                altText={`Logo of ${companyName}`}
                size={'small'}
                className={styles.companyAvatar}
              />
              <p className={styles.companyName}>
                <b>{companyName}</b> {bannerData.message}
              </p>
            </div>
          </div>
        </div>
        <ButtonV3
          className={styles.notifyAction}
          color="secondary"
          size="small"
          isLoading={isLoading}
          onClick={bannerData.action}
        >
          {bannerData.actionText}
        </ButtonV3>
      </div>
    </>
  );
};
